// StudentHomeScreen.js
// Home redesign (Option A, 2026-09-16): "what do I do next?" layout built from
// the home/student modules and useStudentHomeData.
// See docs/superpowers/specs/2026-09-16-home-redesign.md §1, §6.
import React, { useMemo } from "react";
import { View, ScrollView, StyleSheet, useWindowDimensions } from "react-native";

import { HOME, homeColors } from "../components/home/homeTheme";
import { relativeDay } from "../components/home/homeTime";
import { STEM_MISSIONS, SUMMER_CAMP_DAYS } from "../components/home/launcherCatalog";
import useStudentHomeData from "../components/home/hooks/useStudentHomeData";

import HomeRowGrid from "../components/home/primitives/HomeRowGrid";
import GreetingBanner from "../components/home/GreetingBanner";
import ContinueCard from "../components/home/student/ContinueCard";
import WeekStrip from "../components/home/student/WeekStrip";
import DueSoonList from "../components/home/student/DueSoonList";
import SuggestedNextList from "../components/home/student/SuggestedNextList";
import RecentList from "../components/home/student/RecentList";
import ExploreSection from "../components/home/student/ExploreSection";

import {
  ResumeCardStep,
  StudentRecentQuizzesStep,
  UpcomingTestsStep,
  StreakCardStep,
  RecentActivitiesStep,
} from "../components/WalkthroughSteps";

const identity = (s) => s;
const DESKTOP_BREAKPOINT = 1024;

/** Mirrors ContinueCard's own "is there anything to resume" check (§5). */
const hasResumeContent = (learningResumeState, curiosityResumeState) => {
  const assetType = learningResumeState?.asset_type?.toUpperCase?.() || "";
  const hasLearning = assetType === "VIDEO" || assetType === "PWO";
  const hasCuriosity = !!curiosityResumeState?.ai_pointer?.conversation_id;
  return hasLearning || hasCuriosity;
};

/** Most recently created practice test, or null. */
const mostRecentPracticeTest = (myPracticeTests) => {
  const list = Array.isArray(myPracticeTests) ? myPracticeTests : [];
  if (list.length === 0) return null;
  return [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
};

/**
 * StudentHomeScreen - "what do I do next?" home layout.
 *
 * Layout (desktop >=1024px): [ContinueCard 2fr | WeekStrip 1fr],
 * [DueSoonList | SuggestedNextList], ExploreSection, RecentList.
 * Below 1024px: single column, same order.
 */
const StudentHomeScreen = ({
  // User data
  userName = "Student",
  userEmail = null,
  authToken = null,

  // Resume states from API
  learningResumeState = null,
  curiosityResumeState = null,

  // Tests data. `availableTests` is new (WP7 will start passing it
  // separately from `upcomingTests`); when it is undefined, `upcomingTests`
  // is treated as the already-merged list, matching today's caller.
  availableTests,
  upcomingTests = [],
  myTestResults = [],
  myPracticeTests = [],
  onPracticeTestPress,

  // Navigation callbacks
  onContinueLearning,
  onContinueCuriosityAI,
  onBrowseTopics,
  onTestPress,
  onViewAllTests,
  onActivityPress,
  onRecommendationPress,
  onOpenJourney,
  showJourneyLauncher = false,
  showStemMissionLauncher = false,

  // Lesson in a Box
  lessonPlans = [],
  lessonPlansProgress = {},
  lessonPlansLoading = false,
  lessonPlansError = null,
  onOpenLessonPlan,

  // Theme
  isDarkMode = false,

  // Translation
  t = identity,
  lang,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const colors = homeColors(isDarkMode);

  const hookAvailableTests = availableTests !== undefined ? availableTests : upcomingTests;
  const hasResume = hasResumeContent(learningResumeState, curiosityResumeState);

  const { week, dueSoon, suggestions, recent } = useStudentHomeData({
    userEmail,
    lang,
    availableTests: hookAvailableTests,
    upcomingTests,
    myTestResults,
    hasResume,
    t,
  });

  const practiceTest = useMemo(() => mostRecentPracticeTest(myPracticeTests), [myPracticeTests]);

  // §6: fold the single most recent practice test into Due soon as a
  // "Practice/info" row instead of a separate card.
  const dueSoonItems = useMemo(() => {
    if (!practiceTest) return dueSoon;
    const practiceItem = {
      test: { ...practiceTest, __isPractice: true },
      tag: { label: t("Practice"), tone: "info" },
      subtitle: relativeDay(practiceTest.created_at, new Date(), t),
    };
    return [...dueSoon, practiceItem].slice(0, HOME.maxDueSoon);
  }, [dueSoon, practiceTest, t]);

  const handleDueSoonPress = (test) => {
    if (test?.__isPractice) {
      onPracticeTestPress?.(test);
    } else {
      onTestPress?.(test);
    }
  };

  // DueSoonList/SuggestedNextList render null when their items are empty;
  // precompute that here (rather than always passing the element) so the
  // desktop HomeRowGrid below can actually drop the empty column instead of
  // reserving its flex share next to a null render.
  const dueSoonNode =
    dueSoonItems.length > 0 ? (
      <DueSoonList
        items={dueSoonItems}
        onTestPress={handleDueSoonPress}
        onViewAll={onViewAllTests}
        isDarkMode={isDarkMode}
        t={t}
      />
    ) : null;
  const suggestedNode =
    suggestions.length > 0 ? (
      <SuggestedNextList
        items={suggestions}
        onRecommendationPress={onRecommendationPress}
        isDarkMode={isDarkMode}
        t={t}
      />
    ) : null;

  const showLessonFinder = !!onOpenLessonPlan;
  const lessonFinderProps = {
    lessons: lessonPlans,
    progressByLesson: lessonPlansProgress,
    loading: lessonPlansLoading,
    error: lessonPlansError,
    onOpenLesson: onOpenLessonPlan,
    audience: "student",
    subtitle: t("Work through each lesson at your own pace."),
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.content}>
        <GreetingBanner
          userName={userName}
          isDarkMode={isDarkMode}
          t={t}
          lang={lang}
          style={styles.greeting}
        />

        {isDesktop ? (
          <HomeRowGrid
            testID="student-home-continue-row"
            style={styles.block}
            columns={[
              {
                node: (
                  <ResumeCardStep>
                    <ContinueCard
                      learningResumeState={learningResumeState}
                      curiosityResumeState={curiosityResumeState}
                      onContinueLearning={onContinueLearning}
                      onContinueCuriosityAI={onContinueCuriosityAI}
                      onBrowseTopics={onBrowseTopics}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </ResumeCardStep>
                ),
                flex: 2,
              },
              {
                node: (
                  <StudentRecentQuizzesStep>
                    <WeekStrip week={week} isDarkMode={isDarkMode} t={t} />
                  </StudentRecentQuizzesStep>
                ),
                flex: 1,
              },
            ]}
          />
        ) : (
          <>
            <ResumeCardStep style={styles.block}>
              <ContinueCard
                learningResumeState={learningResumeState}
                curiosityResumeState={curiosityResumeState}
                onContinueLearning={onContinueLearning}
                onContinueCuriosityAI={onContinueCuriosityAI}
                onBrowseTopics={onBrowseTopics}
                isDarkMode={isDarkMode}
                t={t}
              />
            </ResumeCardStep>
            <StudentRecentQuizzesStep style={styles.block}>
              <WeekStrip week={week} isDarkMode={isDarkMode} t={t} />
            </StudentRecentQuizzesStep>
          </>
        )}

        {isDesktop ? (
          <HomeRowGrid
            testID="student-home-due-soon-row"
            style={styles.block}
            columns={[
              { node: dueSoonNode ? <UpcomingTestsStep>{dueSoonNode}</UpcomingTestsStep> : null, flex: 1 },
              { node: suggestedNode ? <StreakCardStep>{suggestedNode}</StreakCardStep> : null, flex: 1 },
            ]}
          />
        ) : (
          <>
            <UpcomingTestsStep style={styles.block}>{dueSoonNode}</UpcomingTestsStep>
            <StreakCardStep style={styles.block}>{suggestedNode}</StreakCardStep>
          </>
        )}

        <ExploreSection
          showLessonFinder={showLessonFinder}
          lessonFinderProps={lessonFinderProps}
          showMissions={showStemMissionLauncher}
          showJourneys={showJourneyLauncher}
          onOpenJourney={onOpenJourney}
          missionCatalog={STEM_MISSIONS}
          dayCatalog={SUMMER_CAMP_DAYS}
          studentEmail={userEmail}
          authToken={authToken}
          isDarkMode={isDarkMode}
          t={t}
        />

        <RecentActivitiesStep style={styles.block}>
          <RecentList
            activities={recent}
            onActivityPress={onActivityPress}
            isDarkMode={isDarkMode}
            t={t}
          />
        </RecentActivitiesStep>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { flexGrow: 1, paddingBottom: 24 },
  content: { padding: 24 },
  greeting: { marginBottom: HOME.gap },
  block: { marginBottom: HOME.gap },
});

export default StudentHomeScreen;
